/**
 * @file LookBackgroundGroup.tsx
 * @description Группа «Фон» вкладки «Оформление» секции (phase19.16 Р2;
 * uiux-design-r2 §2, §3): [Прозрачный][Цвет][Фото]. Прозрачный — виден фон
 * страницы (ссылка «Фон страницы →»); цвет — роль сайта или «Свой цвет»
 * (решение 2026-10-11, дополнение SB8).
 *
 * SB120 этап 1 (uiux-sb120-stage1 §2.3, §2.4; вопросы 4, 7, 8): у фото —
 * точка фокуса для ПК и телефона с рамкой «░ уйдёт за край», [Кадр…]
 * [Заменить] [Убрать фото], шаг «Кадр под блок» сразу после выбора файла
 * (файл не режется), «Другое фото для телефона» (`mediaIdPhone`). Новое фото
 * у блока «по содержимому» получает высоту «По фото» / «По кадру телефона
 * 4:5». Надписей поверх фона у секции нет — они у элемента «Фото».
 */

import { DEFAULT_SITE_THEME, type SectionBg, type SectionLook } from "@chaiberi/shared";
import { Crop } from "lucide-react";
import { useState } from "react";

import { Button } from "../../../ui/button";
import { JUMP_LINK_CLASS } from "../../../ui/jump-link";
import { MediaPickerDialog } from "../../../site-view/media/MediaPickerDialog";
import { Segmented } from "../../../site-view/Segmented";
import { SurfaceFillField } from "../../../site-view/SurfaceFillField";
import { useSectionForm } from "../section-form-context";
import { BgFrameStep } from "./BgFrameStep";
import { LookPhotoOptions } from "./LookPhotoOptions";
import { PhotoFocusField } from "./PhotoFocusField";
import { LookWarningNote } from "../fields/LookWarningNote";
import { rememberMediaSize } from "../../../../hooks/site-builder/useMediaSizes";
import { withPhotoBackground } from "../../../../lib/site-builder/section-edit";
import { bgToFrame, frameToBg, type FrameDraft } from "../../../../lib/site-builder/frame-draft";
import { BG_ROLE_LABELS } from "../../../../lib/site-builder/section-meta";
import type { Device } from "../../../../lib/site-builder/look-size";

/** Режим фона: свой цвет — тот же режим «Цвет», что и роль. */
type Mode = Exclude<SectionBg["kind"], "custom">;

const MODES = [
  { value: "transparent" as const, label: "Прозрачный" },
  { value: "role" as const, label: "Цвет" },
  { value: "media" as const, label: "Фото" },
];

interface Props {
  look: SectionLook;
  onChange: (look: SectionLook) => void;
  /** Сразу открыть медиатеку (заготовка с фото-фоном). */
  askPhoto?: boolean;
  onAsked?: () => void;
  /** «Фон страницы →» — к настройкам страницы. */
  onPageBackground?: () => void;
}

export function LookBackgroundGroup({ look, onChange, askPhoto = false, onAsked, onPageBackground }: Props) {
  // Что выбираем в медиатеке: фото фона или своё фото телефона.
  const [picking, setPicking] = useState<Device | null>(askPhoto ? "desktop" : null);
  const [framing, setFraming] = useState<Device | null>(null);
  // Черновик кадра, пока выбирают фото телефона (в форму — только по «Готово»).
  const [pending, setPending] = useState<FrameDraft | null>(null);
  const { theme, mediaSizes } = useSectionForm();
  const colors = theme?.colors ?? DEFAULT_SITE_THEME.colors;
  const bg = look.bg;
  const mode: Mode = bg.kind === "custom" ? "role" : bg.kind;
  const setBg = (next: SectionBg) => onChange({ ...look, bg: next });

  function onMode(next: Mode) {
    if (next === "transparent") setBg({ kind: "transparent" });
    else if (next === "role") setBg(bg.kind === "role" || bg.kind === "custom" ? bg : { kind: "role", role: "panelBg" });
    else setPicking("desktop");
  }
  function closePicker() {
    // Закрыли медиатеку фото телефона — назад к кадру, форма без изменений.
    if (picking === "phone" && pending) setFraming("phone");
    setPicking(null);
    onAsked?.();
  }
  function onPicked(media: { id: string; width: number | null; height: number | null }) {
    rememberMediaSize(media);
    const target = picking;
    setPicking(null);
    onAsked?.();
    if (target === "phone") {
      if (pending) setPending({ ...pending, mediaIdPhone: media.id });
      setFraming("phone");
      return;
    }
    setPending(null);
    onChange(withPhotoBackground(look, media.id));
    // Сразу — кадр под блок (вопрос 4): «Пропустить» есть всегда.
    setFraming(target ?? "desktop");
  }

  return (
    <div className="flex flex-col gap-3">
      <Segmented aria-label="Фон секции" options={MODES} value={mode} onChange={onMode} />
      {bg.kind === "transparent" ? (
        <p className="type-caption text-on-surface-variant">
          Виден фон страницы.{" "}
          {onPageBackground ? (
            <button type="button" className={JUMP_LINK_CLASS} onClick={onPageBackground}>
              Фон страницы →
            </button>
          ) : null}
        </p>
      ) : null}
      {bg.kind === "role" || bg.kind === "custom" ? (
        <SurfaceFillField label="Цвет фона секции" value={bg} colors={colors} labels={BG_ROLE_LABELS} onChange={(fill) => setBg(fill)} />
      ) : null}
      {bg.kind === "media" ? (
        <>
          <PhotoFocusField look={look} bg={bg} onChange={setBg} />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setFraming("desktop")}>
              <Crop aria-hidden />
              Кадр…
            </Button>
            <Button variant="outline" onClick={() => setPicking("desktop")}>
              Заменить
            </Button>
            <Button variant="ghost" onClick={() => setBg({ kind: "transparent" })}>
              Убрать фото
            </Button>
          </div>
          {/* Почему «!» у «Фона» — здесь же, а не только в окне «Кадр…» и в общем списке. */}
          <LookWarningNote paths={["look.bg.mediaId", "look.bg.mediaIdPhone"]} fileWidthOf={(path) => mediaSizes[path.endsWith("mediaIdPhone") ? (bg.mediaIdPhone ?? bg.mediaId) : bg.mediaId]?.width} />
          <LookPhotoOptions bg={bg} onChange={setBg} />
          <BgFrameStep
            open={framing !== null}
            device={framing ?? "desktop"}
            look={look}
            bg={bg}
            initial={pending ?? bgToFrame(bg)}
            onClose={() => {
              setFraming(null);
              setPending(null);
            }}
            onDone={(draft) => {
              setFraming(null);
              setPending(null);
              setBg(frameToBg(bg, draft));
            }}
            onPickPhone={(draft) => {
              // Черновик кадра ждёт в окне: в форму — только по «Готово».
              setPending(draft);
              setFraming(null);
              setPicking("phone");
            }}
          />
        </>
      ) : null}
      <MediaPickerDialog
        open={picking !== null}
        title={picking === "phone" ? "Фото фона для телефона" : "Фото фона секции"}
        hint={picking === "phone" ? "Узкое фото 4:5 или 9:16 — для телефона." : "Широкое фото от 1600px. Текст на фото останется читаемым с затемнением 20–40 %."}
        onClose={closePicker}
        onSelect={onPicked}
      />
    </div>
  );
}
